<script setup>
defineOptions({
    name: 'ContractorZeMaterialsSelectionModal',
});

/*
|--------------------------------------------------------------------------
| ContractorZeMaterialsSelectionModal.vue
|--------------------------------------------------------------------------
| Purpose
|   Modal for selecting a contractor before opening its cluster Zero Emission
|   material catalogue ("Zero Emissie materieelstukken"). Opened from the
|   sidebar "Materieelstukken" entry. The dropdown only lists cluster-linked
|   contractors the user may manage (admins see all, SR coordinators see their
|   own) — scoping is enforced by the search endpoint and policy.
|
| Usage (parent):
|   <ContractorZeMaterialsSelectionModal v-model="show" @select="onSelect" />
|
| Props
|   - modelValue (Boolean|String): controls modal visibility via v-model
|
| Events
|   - update:modelValue(Boolean): emitted when the modal opens/closes
|   - select(Object): emitted when the user chooses a contractor
|   - close: emitted when modal is closed
|
| Workflow
|   1) Setup & routing helper
|   2) Props & emits
|   3) Reactive state
|   4) Methods: handleSelect, close
*/

import { ref, computed, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Modal from '@/components/Modal.vue';
import FormRichSelect from '@/components/Form/FormRichSelect.vue';

/* Routing helper (Ziggy route) */
const instance = getCurrentInstance();
const route = instance?.appContext.config.globalProperties.route;

/* Props & Emits */
const props = defineProps({
    /** Controls modal visibility - accepts boolean or truthy value (e.g. menu key) */
    modelValue: { type: [Boolean, String], default: false },
});

const emit = defineEmits(['update:modelValue', 'select', 'close']);

/* v-model adapter for modal visibility - converts to boolean for Modal component */
const show = computed({
    get: () => !!props.modelValue,
    set: (val) => emit('update:modelValue', val),
});

/* Reactive state */
const selectedContractor = ref(null);

/* Search URL for FormRichSelect remote search (cluster-linked, scoped contractors) */
const searchUrl = computed(() => route('contractors.ze-materials.search'));

/**
 * Normalise the selected option to { id, name }.
 */
function getContractorSelection(selectedValue) {
    if (selectedValue && typeof selectedValue === 'object') {
        return {
            id: selectedValue.id ?? selectedValue.value ?? null,
            name: selectedValue.label ?? selectedValue.name ?? '',
        };
    }

    return { id: selectedValue, name: '' };
}

/**
 * Handle contractor selection: navigate to the contractor's Zero Emission
 * material catalogue.
 *
 * @route GET /opdrachtnemers/{contractor}/zero-emissie-materieelstukken
 */
function handleSelect() {
    if (!selectedContractor.value) return;

    const contractorSelection = getContractorSelection(selectedContractor.value);
    if (!contractorSelection.id) return;

    emit('select', {
        contractor_id: contractorSelection.id,
        contractor_name: contractorSelection.name,
    });

    router.visit(route('contractors.ze-materials.index', { contractor: contractorSelection.id }));

    close();
}

/**
 * Close the modal and reset state.
 */
function close() {
    show.value = false;
    selectedContractor.value = null;
    emit('close');
}
</script>

<template>
    <Modal
        context="zero-emissie-materieelstukken-opdrachtnemer-kiezen"
        :show="show"
        title="Zero Emissie materieelstukken"
        size="md"
        theme="green"
        confirm-button-variant="green"
        :confirm-button-disabled="!selectedContractor"
        :show-default-footer="true"
        @close="close"
        @cancel="close"
        @confirm="handleSelect"
        :cancel-button-id="'input-ze-materials-contractor-modal-cancel'"
        :confirm-button-id="'input-ze-materials-contractor-modal-select'"
    >
        <div class="contractor-selection-modal-content">
            <!-- Description -->
            <p class="modal-description">Selecteer een opdrachtnemer om de zero emissie materieelstukken te beheren.</p>

            <!-- Contractor Dropdown -->
            <div class="contractor-selection-field">
                <label for="input-ze-materials-contractor-selection" class="form-label"> Opdrachtnemer </label>
                <FormRichSelect
                    id="input-ze-materials-contractor-selection"
                    v-model="selectedContractor"
                    :search-url="searchUrl"
                    placeholder="Zoek op naam..."
                    option-label="label"
                    option-value="value"
                    return-object
                />
            </div>
        </div>
    </Modal>
</template>
